/**
 * Employee QC Violations tab.
 *
 * Shows APPROVED violations on the employee's file.
 * `deduction_recommended` is rendered as an ADVISORY badge — the HR system
 * does not compute or apply any deduction amount.
 *
 * HR Manager / HR Admin / Super Admin can:
 *   - REMOVE a violation from the file (reversible; keeps the record).
 *   - CHANGE STATUS to pending / rejected (for accidental approvals).
 * Every action requires a ≥10-char reason and is audit-logged on the backend.
 * Other roles see the tab as read-only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { IssuedViolationRecord } from './IssuedViolationRecord';
import { IssueWarningButton } from '@/components/qc/IssueWarningButton';
import {
  AlertTriangle,
  Info,
  Loader2,
  Trash2,
  ArrowRightLeft,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import QCEvidence from '@/components/qc/QCEvidence';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Roles that can remove / change-status. Backend enforces the same set.
const MUTATION_ROLES = new Set(['hr_manager', 'hr_admin', 'super_admin']);

// Same map as the backend and QCViolationsPage. From `approved` (the only
// state visible in this tab) HR may go back to pending, reject, or remove.
const ALLOWED_TARGETS = {
  approved: ['pending_hr_approval', 'rejected', 'unapproved_hr_removed'],
};

const severityBadge = (sev) => ({
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
}[sev] || 'bg-slate-100 text-slate-700');

const statusLabel = (status, isRTL) => ({
  pending_hr_approval: isRTL ? 'قيد الانتظار' : 'Pending',
  approved: isRTL ? 'معتمد' : 'Approved',
  rejected: isRTL ? 'مرفوض' : 'Rejected',
  unapproved_hr_removed: isRTL ? 'مُزال من الملف' : 'Removed from file',
}[status] || status);

const formatWhen = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const EmployeeQCViolationsTab = ({ employeeId }) => {
  const [includeWithdrawn, setIncludeWithdrawn] = useState(false);
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const t = (en, ar) => (isRTL ? ar : en);
  const canMutate = useMemo(() => MUTATION_ROLES.has(user?.role), [user]);

  const [items, setItems] = useState([]);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [removeFor, setRemoveFor] = useState(null);
  const [removeReason, setRemoveReason] = useState('');
  const [changeFor, setChangeFor] = useState(null);
  const [changeTarget, setChangeTarget] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchViolations = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API_URL}/qc-violations/employee/${employeeId}`,
        { headers: { Authorization: `Bearer ${token}` }, params: { include_withdrawn: includeWithdrawn } },
      );
      setItems(r.data.items || []);
      setNotice(r.data.notice || '');
    } catch (e) {
      setError(e.response?.data?.detail?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [employeeId, token, includeWithdrawn]);

  useEffect(() => { fetchViolations(); }, [fetchViolations]);

  const doRemove = async () => {
    if (!removeFor) return;
    if (removeReason.trim().length < 10) {
      toast.error(t('Reason must be at least 10 characters',
        'يجب أن يكون السبب 10 أحرف على الأقل'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/qc-violations/${removeFor.id}/remove`,
        { reason: removeReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(t('Violation removed from file', 'تم إزالة المخالفة من الملف'));
      setRemoveFor(null);
      setRemoveReason('');
      fetchViolations();
    } catch (e) {
      toast.error(
        e.response?.data?.detail?.error ||
        (Array.isArray(e.response?.data?.detail) ? e.response.data.detail[0]?.msg : null) ||
        e.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openChange = (row) => {
    setChangeFor(row);
    const targets = ALLOWED_TARGETS[row.status] || [];
    setChangeTarget(targets[0] || '');
    setChangeReason('');
  };

  const doChangeStatus = async () => {
    if (!changeFor || !changeTarget) return;
    if (changeReason.trim().length < 10) {
      toast.error(t('Reason must be at least 10 characters',
        'يجب أن يكون السبب 10 أحرف على الأقل'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/qc-violations/${changeFor.id}/change-status`,
        { new_status: changeTarget, reason: changeReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(t('Status changed', 'تم تغيير الحالة'));
      setChangeFor(null);
      setChangeTarget('');
      setChangeReason('');
      fetchViolations();
    } catch (e) {
      toast.error(
        e.response?.data?.detail?.error ||
        (Array.isArray(e.response?.data?.detail) ? e.response.data.detail[0]?.msg : null) ||
        e.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="qc-emp-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-rose-600" data-testid="qc-emp-error">{error}</div>
    );
  }

  return (
    <div className="space-y-4" data-testid="qc-emp-tab">
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={includeWithdrawn} onChange={e => setIncludeWithdrawn(e.target.checked)} data-testid="qc-issued-history-toggle" />{t('Include withdrawn history', 'إظهار السجل المسحوب')}</label>
      {notice && (
        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm flex gap-2">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500" data-testid="qc-emp-empty">
            {t('No approved QC violations on this employee\'s file.',
               'لا توجد مخالفات جودة معتمدة على ملف هذا الموظف.')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((v) => (
            <Card key={v.id} data-testid={`qc-emp-item-${v.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      {v.violation_item}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {t('Source ID:', 'المعرف:')} <code>{v.source_violation_id}</code>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {v.issue_record?.status === 'withdrawn' && <Badge data-testid={`issued-withdrawn-${v.id}`} variant="secondary">Withdrawn</Badge>}
                    <IssuedViolationRecord violation={v} isRTL={isRTL} />
                    <IssueWarningButton violation={v} isRTL={isRTL} onIssued={fetchViolations} />
                    <Badge className={severityBadge(v.severity)}>{v.severity}</Badge>
                    {v.deduction_recommended && (
                      <Badge
                        className="bg-amber-100 text-amber-800 border border-amber-300"
                        data-testid={`qc-emp-deduction-${v.id}`}
                      >
                        {t('Deduction: recommendation only',
                           'خصم: توصية فقط')}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="text-sm text-slate-700 whitespace-pre-wrap">
                  {v.description}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-500">
                  <div>
                    <div className="uppercase tracking-wide">{t('Occurred', 'التاريخ')}</div>
                    <div className="text-slate-800">{formatWhen(v.occurred_at)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide">{t('Inspector', 'المفتش')}</div>
                    <div className="text-slate-800">{v.inspector_name}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide">{t('Approved by', 'اعتمد بواسطة')}</div>
                    <div className="text-slate-800">{v.decided_by_email || '—'}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide">{t('Approved at', 'وقت الاعتماد')}</div>
                    <div className="text-slate-800">{formatWhen(v.decided_at)}</div>
                  </div>
                </div>

                {v.evidence_state !== 'none' && (
                  <div data-testid={`qc-emp-evidence-${v.id}`}>
                    <QCEvidence
                      violation={v}
                      token={token}
                      isRTL={isRTL}
                      testId={`qc-emp-evidence-${v.id}`}
                    />
                  </div>
                )}

                {canMutate && v.status === 'approved' && (
                  <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRemoveFor(v); setRemoveReason(''); }}
                      disabled={submitting}
                      data-testid={`qc-emp-remove-btn-${v.id}`}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('Remove from file', 'إزالة من الملف')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openChange(v)}
                      disabled={submitting}
                      data-testid={`qc-emp-change-btn-${v.id}`}
                    >
                      <ArrowRightLeft className="h-4 w-4 mr-1" />
                      {t('Change status', 'تغيير الحالة')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Remove dialog */}
      <Dialog
        open={!!removeFor}
        onOpenChange={(o) => { if (!o) { setRemoveFor(null); setRemoveReason(''); } }}
      >
        <DialogContent data-testid="qc-emp-remove-dialog">
          <DialogHeader>
            <DialogTitle>{t('Remove violation from file', 'إزالة المخالفة من الملف')}</DialogTitle>
            <DialogDescription>
              {t(
                'This takes the violation off the employee\'s file but keeps the record for history. It is fully reversible from the QC Violations page (Removed tab). Reason is mandatory (min 10 chars).',
                'ستتم إزالة المخالفة من ملف الموظف مع الاحتفاظ بالسجل في التاريخ. يمكن الإرجاع من صفحة مخالفات ضبط الجودة (تبويب المُزال). السبب مطلوب (10 أحرف على الأقل).'
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            placeholder={t('Reason...', 'السبب...')}
            data-testid="qc-emp-remove-reason-input"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRemoveFor(null); setRemoveReason(''); }}
              data-testid="qc-emp-remove-cancel-btn"
            >
              {t('Cancel', 'إلغاء')}
            </Button>
            <Button
              onClick={doRemove}
              disabled={submitting || removeReason.trim().length < 10}
              data-testid="qc-emp-remove-submit-btn"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                : <Trash2 className="h-4 w-4 mr-1" />}
              {t('Confirm remove', 'تأكيد الإزالة')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change status dialog */}
      <Dialog
        open={!!changeFor}
        onOpenChange={(o) => {
          if (!o) {
            setChangeFor(null); setChangeTarget(''); setChangeReason('');
          }
        }}
      >
        <DialogContent data-testid="qc-emp-change-dialog">
          <DialogHeader>
            <DialogTitle>{t('Change violation status', 'تغيير حالة المخالفة')}</DialogTitle>
            <DialogDescription>
              {t(
                'Content (description, severity, inspector) is not editable — that data comes from the ops platform. Only the status can be changed here. Reason is mandatory (min 10 chars).',
                'المحتوى (الوصف، الشدة، المفتش) غير قابل للتعديل — تلك البيانات مصدرها منصة العمليات. يمكن هنا تغيير الحالة فقط. السبب مطلوب (10 أحرف على الأقل).'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-500 mb-1">{t('New status', 'الحالة الجديدة')}</div>
              <Select value={changeTarget} onValueChange={setChangeTarget}>
                <SelectTrigger data-testid="qc-emp-change-target-select">
                  <SelectValue placeholder={t('Select target status', 'اختر الحالة الهدف')} />
                </SelectTrigger>
                <SelectContent>
                  {(ALLOWED_TARGETS[changeFor?.status] || []).map((st) => (
                    <SelectItem
                      key={st}
                      value={st}
                      data-testid={`qc-emp-change-target-option-${st}`}
                    >
                      {statusLabel(st, isRTL)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              rows={4}
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder={t('Reason...', 'السبب...')}
              data-testid="qc-emp-change-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setChangeFor(null); setChangeTarget(''); setChangeReason('');
              }}
              data-testid="qc-emp-change-cancel-btn"
            >
              {t('Cancel', 'إلغاء')}
            </Button>
            <Button
              onClick={doChangeStatus}
              disabled={submitting || !changeTarget || changeReason.trim().length < 10}
              data-testid="qc-emp-change-submit-btn"
            >
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                : <ArrowRightLeft className="h-4 w-4 mr-1" />}
              {t('Confirm change', 'تأكيد التغيير')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeQCViolationsTab;
